// 📄 components/organizacion/EditarOrgModal.tsx
//
// "Renombrar + Archivar" (2026-08-20) para USUARIOS NORMALES — la misma
// funcionalidad ya vivía en OrganizationsSection.tsx, pero esa pantalla
// cuelga de /admin (AdminRoute), así que solo un Admin de plataforma podía
// llegar ahí. Un owner de equipo que no es Admin no tenía forma de
// renombrar/archivar SU PROPIO equipo — este modal cierra ese hueco,
// disparado desde OrgSwitcher.tsx (topbar, visible para cualquier
// usuario). Mismo estilo que CreateTeamOrgModal.tsx.
import React, { useEffect, useState } from 'react';
import { X, Save, Archive, ArchiveRestore, AlertTriangle, MessageSquare, Trash2, Lock } from 'lucide-react';
import { renameOrganization, setOrganizationArchivada } from '../../services/organizationService.ts';
import { getSlackConfig, setSlackWebhook } from '../../services/slackService.ts';
import { useAuth } from '../../context/AuthContext.tsx';

interface EditarOrgModalProps {
  orgId: string;
  orgNombre: string;
  archivada?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export default function EditarOrgModal({ orgId, orgNombre, archivada, onClose, onSaved }: EditarOrgModalProps) {
  const { profile } = useAuth();
  // "Planes/Suscripciones" (Fase 5/5, 2026-08-26) — este modal es
  // owner-only por construcción (ver comentario de arriba), así que el
  // plan a chequear es siempre el del usuario actual, sin resolver otro uid.
  const tieneSlack = !!profile?.features?.slack;
  const [nombre, setNombre] = useState(orgNombre);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Notificaciones más ricas" (2026-08-20, Fase 3) — webhook de Slack de
  // esta org. Solo el owner puede leer/escribir esto (ver firestore.rules,
  // organizations/{orgId}/integraciones/slack) — este modal ya es
  // owner-only por construcción (OrgSwitcher.tsx solo ofrece "Editar" al
  // owner), así que el getSlackConfig de acá nunca debería toparse con un
  // permission-denied en el uso normal.
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookGuardado, setWebhookGuardado] = useState<string | null>(null);
  const [loadingSlack, setLoadingSlack] = useState(true);
  const [savingSlack, setSavingSlack] = useState(false);
  const [errorSlack, setErrorSlack] = useState<string | null>(null);

  useEffect(() => {
    getSlackConfig(orgId)
      .then((config) => {
        setWebhookUrl(config?.webhookUrl || '');
        setWebhookGuardado(config?.webhookUrl || null);
      })
      .catch((err) => console.error('Error cargando configuración de Slack:', err))
      .finally(() => setLoadingSlack(false));
  }, [orgId]);

  const handleGuardarSlack = async () => {
    setErrorSlack(null);
    setSavingSlack(true);
    try {
      await setSlackWebhook(orgId, webhookUrl.trim() || null);
      setWebhookGuardado(webhookUrl.trim() || null);
    } catch (err: any) {
      setErrorSlack(err.message || 'Error al guardar el webhook de Slack.');
    } finally {
      setSavingSlack(false);
    }
  };

  const handleQuitarSlack = async () => {
    setErrorSlack(null);
    setSavingSlack(true);
    try {
      await setSlackWebhook(orgId, null);
      setWebhookUrl('');
      setWebhookGuardado(null);
    } catch (err: any) {
      setErrorSlack(err.message || 'Error al quitar el webhook de Slack.');
    } finally {
      setSavingSlack(false);
    }
  };

  const handleRenombrar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim() || nombre.trim() === orgNombre) return;
    setError(null);
    setSaving(true);
    try {
      await renameOrganization(orgId, nombre.trim());
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al renombrar el equipo.');
    } finally {
      setSaving(false);
    }
  };

  const handleArchivar = async () => {
    if (!archivada && !window.confirm(`¿Archivar "${orgNombre}"? No vas a poder crear proyectos ni sumar miembros nuevos hasta que la reactives — todo lo que ya tiene se conserva.`)) return;
    setError(null);
    setSaving(true);
    try {
      await setOrganizationArchivada(orgId, !archivada);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al cambiar el estado del equipo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">Editar equipo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleRenombrar} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">
              {error}
            </div>
          )}

          {archivada && (
            <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-400 rounded-lg p-3 text-xs">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Este equipo está archivado — no admite proyectos ni miembros nuevos hasta que lo reactives.</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Nombre del equipo</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                className="flex-1 bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
              />
              <button
                type="submit"
                disabled={saving || !nombre.trim() || nombre.trim() === orgNombre}
                className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 disabled:opacity-50 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-all"
              >
                <Save className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={handleArchivar}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 disabled:opacity-50 px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 transition-all"
            >
              {archivada ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
              {archivada ? 'Reactivar equipo' : 'Archivar equipo'}
            </button>
            <p className="text-[10px] text-gray-500 mt-1.5 text-center">
              {archivada ? 'Vuelve a admitir proyectos y miembros nuevos.' : 'No borra nada — se puede reactivar cuando quieras.'}
            </p>
          </div>

          <div className="pt-2 border-t border-white/10">
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 mb-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Notificaciones en Slack (opcional)
            </label>
            {!tieneSlack ? (
              <div className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-gray-400">
                <Lock className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-500" />
                <span>No incluida en tu plan actual — pedile a un Admin de la plataforma que te asigne un plan Pro o Enterprise.</span>
              </div>
            ) : loadingSlack ? (
              <p className="text-xs text-gray-500">Cargando...</p>
            ) : (
              <>
                {errorSlack && <p className="text-red-400 text-xs mb-1.5">{errorSlack}</p>}
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://hooks.slack.com/services/..."
                    className="flex-1 bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={handleGuardarSlack}
                    disabled={savingSlack || !webhookUrl.trim() || webhookUrl.trim() === webhookGuardado}
                    title="Guardar webhook"
                    className="flex-shrink-0 flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 disabled:opacity-50 px-3 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                  >
                    <Save className="w-4 h-4" />
                  </button>
                  {webhookGuardado && (
                    <button
                      type="button"
                      onClick={handleQuitarSlack}
                      disabled={savingSlack}
                      title="Quitar webhook"
                      className="flex-shrink-0 flex items-center gap-1.5 bg-white/5 hover:bg-red-500/10 disabled:opacity-50 px-3 py-2 rounded-lg text-sm text-gray-400 hover:text-red-400 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-1.5">
                  Cuando esté configurado, se posteará ahí lo mismo que ya se manda por email: nuevos miembros/invitaciones, incidentes nuevos y casos de prueba fallidos.
                </p>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
